import { Button } from '@mantine/core';
import { DoorOpen } from 'lucide-react';
import { FC, useState } from 'react';

import Paragraph from '@/components/text/Paragraph.tsx';

import { StackSelectionResponse, StackSelectionResult } from '@/models/call-system/StackSelection.ts';

interface Props {
    stacks: string[];
    defaultSelectedStack?: string;
    onConfirm: (response: StackSelectionResponse) => void;
}

const StackSelectionModal: FC<Props> = ({ stacks, defaultSelectedStack, onConfirm }) => {
    const [selectedStack, setSelectedStack] = useState<string | undefined>(defaultSelectedStack);
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = (): void => {
        if (!selectedStack) return;
        setIsLoading(true);
        onConfirm({ result: StackSelectionResult.SELECTED, selectedStack: selectedStack });
    };

    const handleSkipDispatch = (): void => {
        onConfirm({ result: StackSelectionResult.SKIP_DISPATCH });
    };

    const handleCancel = (): void => {
        onConfirm({ result: StackSelectionResult.CANCELLED });
    };

    return (
        <div className="flex flex-col space-y-4">
            <Paragraph className="text-slate-600">
                {stacks.length === 1
                    ? 'Zimmer für die Weiterleitung bestätigen.'
                    : 'Mehrere Zimmer verfügbar. Bitte wählen Sie das Zielzimmer für die Weiterleitung.'}
            </Paragraph>

            <div className="grid grid-cols-2 gap-3">
                {stacks.map((stack) => (
                    <button
                        key={stack}
                        type="button"
                        onClick={() => setSelectedStack(stack)}
                        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 p-4 transition-all ${
                            selectedStack === stack
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        <DoorOpen size={28} className={selectedStack === stack ? 'text-green-600' : 'text-gray-500'} />
                        <span
                            className={`mt-2 text-sm font-semibold ${
                                selectedStack === stack ? 'text-green-700' : 'text-gray-700'
                            }`}
                        >
                            {stack.toUpperCase()}
                        </span>
                    </button>
                ))}
            </div>

            <div className="mt-4 flex flex-col gap-2">
                <Button
                    variant="filled"
                    className="w-full"
                    onClick={handleConfirm}
                    disabled={!selectedStack}
                    loading={isLoading}
                >
                    Ticket weiterleiten
                </Button>
                <Button variant="subtle" color="gray" className="w-full" onClick={handleSkipDispatch}>
                    Ohne Weiterleitung fortfahren
                </Button>
                <Button variant="subtle" color="red" className="w-full" onClick={handleCancel}>
                    Abbrechen
                </Button>
            </div>
        </div>
    );
};

export default StackSelectionModal;
